import type { MarketingNote } from '../../../../api/venue-marketing';

import { useMarketingNotes, type NoteList } from './use-marketing-notes';
import { marketingErrorMessage } from './venue-marketing-errors';

export interface NoteListState {
  notes: MarketingNote[];
  /** How many notes the server holds for this list. */
  total: number;
  /** How many the server sends per page, for the Show more step. */
  pageSize: number | undefined;
  /** The number shown in the heading, or null until the first page arrives. */
  count: number | null;
  status: 'pending' | 'error' | 'success';
  loadError: string | null;
  loadingMore: boolean;
  retry: () => void;
  more: () => void;
}

/** A note added while someone pages can show up on two pages; keep the first. */
function uniqueById(notes: MarketingNote[]): MarketingNote[] {
  const seen = new Set<string>();
  return notes.filter((n) => !seen.has(n.id) && !!seen.add(n.id));
}

/** One list of notes flattened for display, with its heading count and paging. */
export function useNoteList(venueId: string, list: NoteList): NoteListState {
  const query = useMarketingNotes(venueId, list);
  const first = query.data?.pages[0];
  const headingCount = list === 'open' ? first?.openActionItemCount : first?.total;
  return {
    notes: uniqueById(query.data?.pages.flatMap((p) => p.notes) ?? []),
    total: first?.total ?? 0,
    pageSize: first?.pageSize,
    count: headingCount ?? null,
    status: query.status,
    loadError: query.error ? marketingErrorMessage(query.error, 'load-notes') : null,
    loadingMore: query.isFetchingNextPage,
    retry: () => void query.refetch(),
    more: () => void query.fetchNextPage(),
  };
}
